// Mock data for the Visa-Ready Document Checker

export const countries = [
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
];

export const visaTypes = {
  ES: [
    { id: 'work', name: 'Work Visa', description: 'For employment in Spain' },
    { id: 'student', name: 'Student Visa', description: 'For academic studies' },
    { id: 'family', name: 'Family Reunification', description: 'Join family members' },
    { id: 'entrepreneur', name: 'Entrepreneur Visa', description: 'Start a business' },
    { id: 'digital-nomad', name: 'Digital Nomad Visa', description: 'Remote workers' },
    { id: 'non-lucrative', name: 'Non-Lucrative Visa', description: 'Residency without work' },
  ],
  FR: [
    { id: 'work', name: 'Work Visa', description: 'For employment in France' },
    { id: 'student', name: 'Student Visa', description: 'For academic studies' },
  ],
  DE: [
    { id: 'work', name: 'Work Visa', description: 'For employment in Germany' },
    { id: 'student', name: 'Student Visa', description: 'For academic studies' },
  ],
  IT: [
    { id: 'work', name: 'Work Visa', description: 'For employment in Italy' },
    { id: 'student', name: 'Student Visa', description: 'For academic studies' },
  ],
  PT: [
    { id: 'work', name: 'Work Visa', description: 'For employment in Portugal' },
    { id: 'student', name: 'Student Visa', description: 'For academic studies' },
  ],
};

export const documentRequirements = {
  'ES-work': [
    {
      id: 1,
      name: 'Valid Passport',
      category: 'Identity',
      required: true,
      description: 'Must be valid for at least 6 months beyond intended stay',
      commonIssues: ['Expiration within 6 months', 'Missing pages', 'Damaged'],
      processingTime: '1-2 days'
    },
    {
      id: 2,
      name: 'Birth Certificate',
      category: 'Identity',
      required: true,
      description: 'Original or certified copy with apostille',
      commonIssues: ['Missing apostille', 'Incomplete translation', 'Name mismatch'],
      processingTime: '3-5 days'
    },
    {
      id: 3,
      name: 'FBI Background Check',
      category: 'Legal',
      required: true,
      description: 'Must be less than 3 months old with apostille',
      commonIssues: ['Expired document', 'Missing apostille', 'Invalid format'],
      processingTime: '7-10 days'
    },
    {
      id: 4,
      name: 'Work Contract',
      category: 'Employment',
      required: true,
      description: 'Signed employment contract from Spanish employer',
      commonIssues: ['Missing signatures', 'Invalid dates', 'Incorrect format'],
      processingTime: '1-2 days'
    },
    {
      id: 5,
      name: 'Medical Certificate',
      category: 'Health',
      required: true,
      description: 'Recent medical examination certificate',
      commonIssues: ['Outdated', 'Missing required tests', 'Incomplete'],
      processingTime: '2-3 days'
    },
  ],
  'ES-student': [
    {
      id: 1,
      name: 'Valid Passport',
      category: 'Identity',
      required: true,
      description: 'Must be valid for duration of studies',
      commonIssues: ['Expiration within study period', 'Damaged'],
      processingTime: '1-2 days'
    },
    {
      id: 2,
      name: 'University Acceptance Letter',
      category: 'Education',
      required: true,
      description: 'Official acceptance from Spanish institution',
      commonIssues: ['Unofficial copy', 'Missing seal', 'Expired'],
      processingTime: '1-2 days'
    },
    {
      id: 3,
      name: 'Proof of Financial Means',
      category: 'Financial',
      required: true,
      description: 'Bank statements or scholarship proof',
      commonIssues: ['Insufficient funds', 'Outdated statements', 'Invalid format'],
      processingTime: '2-3 days'
    },
  ],
  'ES-family': [
    {
      id: 1,
      name: 'Valid Passport',
      category: 'Identity',
      required: true,
      description: 'Must be valid for intended stay',
      commonIssues: ['Near expiration', 'Damaged'],
      processingTime: '1-2 days'
    },
    {
      id: 2,
      name: 'Marriage Certificate',
      category: 'Family',
      required: true,
      description: 'With apostille and official translation',
      commonIssues: ['Missing apostille', 'Poor translation', 'Name inconsistencies'],
      processingTime: '3-5 days'
    },
    {
      id: 3,
      name: 'Birth Certificate',
      category: 'Family',
      required: true,
      description: 'For dependent children',
      commonIssues: ['Missing apostille', 'Incomplete', 'Translation errors'],
      processingTime: '3-5 days'
    },
  ],
};

// Mock analysis results
export const mockAnalysisResult = {
  documentCategory: 'Birth Certificate',
  readinessScore: 78,
  status: 'warning',
  riskLevel: 'medium',
  criticalIssues: [
    {
      id: 'c1',
      title: 'Missing Apostille',
      description: 'Document appears to lack proper apostille certification required for international recognition.',
      severity: 'critical'
    },
  ],
  warningIssues: [
    {
      id: 'w1',
      title: 'Name Inconsistency Detected',
      description: 'Minor discrepancy in name formatting may cause delays. Consider obtaining a certified correction.',
      severity: 'warning'
    },
    {
      id: 'w2',
      title: 'Document Quality',
      description: 'Some text appears faint or unclear. Consider obtaining a clearer copy.',
      severity: 'warning'
    },
  ],
  passedChecks: [
    {
      id: 'p1',
      title: 'Document Format',
      description: 'Format appears correct and matches standard requirements.',
      severity: 'passed'
    },
    {
      id: 'p2',
      title: 'Date Validity',
      description: 'Document date is within acceptable range.',
      severity: 'passed'
    },
    {
      id: 'p3',
      title: 'Required Fields Present',
      description: 'All mandatory fields appear to be included.',
      severity: 'passed'
    },
  ],
  freeFixInstructions: [
    '1. Obtain an apostille from your state\'s Secretary of State office',
    '2. Verify name spelling matches exactly across all documents',
    '3. Consider requesting a new certified copy for better clarity',
  ],
  premiumFixInstructions: [
    '4. Detailed step-by-step apostille process with office locations and timelines',
    '5. Name correction affidavit template and notarization requirements',
    '6. Professional translation service recommendations',
    '7. Document quality improvement guidelines with examples',
    '8. Common Spanish consulate rejection reasons and how to avoid them',
    '9. Priority processing options and expedited service contacts',
    '10. Checklist for final document package submission',
  ],
};

export const pricingPlans = [
  {
    id: 'single',
    name: 'Single Document',
    price: 10,
    features: [
      '1 document analysis',
      'Privacy-safe processing',
      'Basic readiness score',
      'Critical issues only',
      'Instant results',
    ],
    popular: false,
  },
  {
    id: 'bundle',
    name: 'Document Bundle',
    price: null, // Calculated based on quantity
    features: [
      'Upload multiple documents',
      'Bulk discount pricing',
      'Full readiness scoring',
      'All issue details',
      'Premium insights',
      'Priority support',
    ],
    popular: true,
    isBundle: true,
  },
];

// Bundle pricing calculator
export const calculateBundlePrice = (documentCount) => {
  const basePrice = 10; // per document
  const totalWithoutDiscount = documentCount * basePrice;
  
  // Discount tiers
  let discount = 0;
  if (documentCount >= 10) {
    discount = 0.30; // 30% off for 10+ documents
  } else if (documentCount >= 5) {
    discount = 0.20; // 20% off for 5-9 documents
  } else if (documentCount >= 3) {
    discount = 0.10; // 10% off for 3-4 documents
  }
  
  const finalPrice = Math.round(totalWithoutDiscount * (1 - discount));
  const savings = totalWithoutDiscount - finalPrice;
  
  return {
    documentCount,
    basePrice,
    totalWithoutDiscount,
    discount: discount * 100,
    finalPrice,
    savings,
    perDocumentPrice: (finalPrice / documentCount).toFixed(2)
  };
};
